import React, { useEffect, useRef } from "react";
import { createField } from "../lib/field";
import useRafScroll from "../hooks/useRafScroll";
import useReducedMotion from "../hooks/useReducedMotion";

/** Splits a word into spans so each glyph can be scheduled independently. */
function Kinetic({ word, delay = 0 }) {
  return (
    <span className="kinetic" aria-hidden="true">
      {word.split("").map((ch, i) => (
        <span
          key={`${ch}-${i}`}
          className="kinetic__ch"
          style={{ "--d": `${delay + i * 42}ms` }}
        >
          {ch}
        </span>
      ))}
    </span>
  );
}

export default function Hero({ ready }) {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const innerRef = useRef(null);
  const fieldRef = useRef(null);
  const reduced = useReducedMotion();

  /* --- the magnetic field --- */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || reduced) return undefined;

    const field = createField(canvas, {
      spacing: window.innerWidth < 720 ? 28 : 34,
      radius: window.innerWidth < 720 ? 170 : 270,
      // Read the live theme colour so the field recolours with the page.
      getColor: () => {
        const fg = getComputedStyle(document.documentElement)
          .getPropertyValue("--fg")
          .trim();
        return hexToRgbList(fg) || "237,233,225";
      },
    });

    fieldRef.current = field;
    return () => {
      field.destroy();
      fieldRef.current = null;
    };
  }, [reduced]);

  /* --- parallax + fade as the hero leaves --- */
  useRafScroll(() => {
    const section = sectionRef.current;
    const inner = innerRef.current;
    if (!section || !inner) return;

    const h = window.innerHeight;
    const p = Math.max(0, Math.min(1, -section.getBoundingClientRect().top / h));

    if (!reduced) {
      inner.style.transform = `translate3d(0, ${p * -14}vh, 0)`;
      inner.style.opacity = String(1 - p * 1.25);
    }
    fieldRef.current?.setIntensity(1 - p * 1.4);
  });

  return (
    <section className="hero" id="top" data-act="top" ref={sectionRef}>
      <canvas className="hero__field" ref={canvasRef} aria-hidden="true" />

      <div className={`hero__inner${ready ? " is-ready" : ""}`} ref={innerRef}>
        <p className="hero__eyebrow mono" style={{ "--d": "120ms" }}>
          <span className="hero__pip" />
          Independent mobile studio — two apps live on iOS
        </p>

        <h1 className="hero__title">
          <span className="sr-only">
            AB App Studio. We build apps that break loops.
          </span>

          <span className="hero__line hero__line--1 display">
            <Kinetic word="WE&nbsp;BUILD" delay={260} />
          </span>

          <span className="hero__line hero__line--2">
            <span className="hero__serif serif">apps that</span>
          </span>

          <span className="hero__line hero__line--3 display">
            <Kinetic word="BREAK&nbsp;LOOPS" delay={520} />
          </span>
        </h1>

        <div className="hero__base">
          <p className="hero__blurb" style={{ "--d": "980ms" }}>
            Habits, fear, the 3am deposit. We ship products that put a wedge in
            the loop and measure only what you actually did about it today.
          </p>

          <div className="hero__cta" style={{ "--d": "1120ms" }}>
            <a className="btn" href="#work" data-lens="SEE">
              <span>See the work</span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 4v16M6 14l6 6 6-6" />
              </svg>
            </a>
            <span className="hero__hint mono">Drag your cursor — the field is magnetic</span>
          </div>
        </div>
      </div>

      <div className="hero__rail" aria-hidden="true">
        <span className="mono">SCROLL</span>
        <span className="hero__rail-line" />
      </div>
    </section>
  );
}

/** `#fff9ec` → `"255,249,236"`, so canvas strokes can follow CSS variables. */
function hexToRgbList(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return null;
  return [1, 2, 3].map((i) => parseInt(m[i], 16)).join(",");
}
